import Autocomplete from '@mui/material/Autocomplete';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import is from '@sindresorhus/is';
import { useState } from 'react';
import { Controller } from 'react-hook-form';

import { ToggleChip } from '../../../design/components/interactive-chips/ToggleChip';

import type { AutocompleteProps } from '@mui/material/Autocomplete';
import type { ReactElement } from 'react';
import type { Control, FieldPathValue, FieldValues, Path } from 'react-hook-form';

type BooleanOrUndefined = boolean | undefined;

type WithId = {
    readonly id: string;
};

type ReactHookFormAutocompleteFieldProps<T extends FieldValues> = Omit<
    AutocompleteProps<T, BooleanOrUndefined, BooleanOrUndefined, BooleanOrUndefined>,
    'onBlur' | 'onChange' | 'options' | 'renderInput' | 'value'
> & {
    readonly control: Control<T>;
    readonly errorMessage?: string;
    readonly label: string;
    readonly name: Path<T>;
    readonly options: ReadonlyArray<{ readonly id: string; readonly label: string }>;
};

const areOptionsEqualById = <T extends WithId>(first: T, second: T): boolean => first.id === second.id;

export const ReactHookFormAutocompleteField = <T extends FieldValues>({
    control,
    errorMessage,
    label,
    name,
    options,
}: ReactHookFormAutocompleteFieldProps<T>): ReactElement<any> => {
    const [openOptions, setOpenOptions] = useState<boolean>(false);

    return (
        <Controller
            control={control}
            name={name}
            render={({ field: { onChange, value } }) => {
                const activeValues = is.array(value, is.string)
                    ? options.filter(({ id }) => value.includes(id) === true)
                    : [];

                const removeOption = (option: WithId): void => {
                    if (is.nonEmptyArray(value)) {
                        const filteredOptions = value.filter((val: string) => val !== option.id);
                        onChange(filteredOptions);
                    }
                };

                const getRenderTags = (
                    values: ReactHookFormAutocompleteFieldProps<T>['options'] | readonly [undefined],
                ): ReactElement<any> => (
                    <Grid container={true} gap={1}>
                        {values.map(
                            (value) =>
                                !is.undefined(value) && (
                                    <ToggleChip
                                        key={value.id}
                                        isActive={true}
                                        label={value.label}
                                        onClick={() => removeOption(value)}
                                    />
                                ),
                        )}
                    </Grid>
                );

                return (
                    <Autocomplete
                        fullWidth={true}
                        isOptionEqualToValue={areOptionsEqualById}
                        multiple={true}
                        open={openOptions}
                        options={options}
                        renderInput={(params) => (
                            <TextField
                                // eslint-disable-next-line react/jsx-props-no-spreading
                                {...params}
                                autoComplete="off"
                                helperText={errorMessage}
                                label={label}
                                name={name}
                                size="small"
                                sx={{ '& .MuiAutocomplete-input': { width: 1 } }}
                                variant="outlined"
                            />
                        )}
                        renderTags={getRenderTags}
                        value={activeValues}
                        onChange={(_event, value) => onChange(value.map(({ id }) => id) as FieldPathValue<T, Path<T>>)}
                        onClose={() => setOpenOptions(false)}
                        onOpen={() => setOpenOptions(true)}
                    />
                );
            }}
        />
    );
};
